import { type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@ui/Form";
import { RadioGroup, RadioGroupItem } from "@ui/RadioGroup";
import { BILLING_TYPES, type BillingType } from "@/types/Form";
import { useMultiStepForm } from "@/hooks";
import { type Step3FormKeys } from "@/utils/step3Schema";
import type { StepProps } from "../StepProps";
import { DynamicBillingSubform } from "./DynamicBillingSubform";
import { FixedBillingSubform } from "./FixedBillingSubform";
import { TariffBillingSubform } from "./TariffBillingSubform";

const fields: Step3FormKeys[] = ["margin", "fixedFee", "fixedPrice", "tariff"];

const options = {
  shouldDirty: false,
  shouldTouch: false,
  shouldValidate: false,
};

export const Step3: FC<StepProps> = ({ additionalItems }) => {
  const { t } = useTranslation();
  const { form } = useMultiStepForm();

  const billingType = form.watch("step3.billingType");

  const clearBillingTypeErrors = () => {
    form.setValue("step3.margin", "", options);
    form.setValue("step3.fixedFee", "", options);
    form.setValue("step3.fixedPrice", "", options);
    form.setValue("step3.tariff", undefined, options);
    fields.forEach(f => form.clearErrors(`step3.${f}`));
  };

  const SUBFORMS: Record<BillingType, ReactNode> = {
    dynamic: <DynamicBillingSubform disabled={billingType !== "dynamic"} />,
    constant: <FixedBillingSubform disabled={billingType !== "constant"} />,
    tariff: <TariffBillingSubform disabled={billingType !== "tariff"} />,
  };

  return (
    <>
      <FormField
        control={form.control}
        name="step3.billingType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("step3.q1")}</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={value => {
                  field.onChange(value);
                  clearBillingTypeErrors();
                }}
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                value={field.value ?? ""}
                className="flex flex-col space-y-3 sm:px-4"
              >
                {BILLING_TYPES.map(type => (
                  <FormItem className="flex flex-col space-x-3" key={type}>
                    <div className="flex items-center space-x-3">
                      <FormControl>
                        <RadioGroupItem value={type} id={type} data-test={`billing-${type}`} />
                      </FormControl>
                      <FormLabel htmlFor={type} className="cursor-pointer font-normal">
                        {t(`step3.q1_${type}`)}
                      </FormLabel>
                    </div>
                    {SUBFORMS[type]}
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {additionalItems}
    </>
  );
};
